"use client";

import { useState, useEffect, type SetStateAction, type Dispatch } from "react";

import { useCookies } from "next-client-cookies";
import dappConfig from "@/../config/dapp.config";

export function usePersistState<S>(key: string, initialState?: S, usePrefix: boolean = true, skipDecode: boolean = false): [S, Dispatch<SetStateAction<S>>] {
  const cookies = useCookies();
  const cookieKey = `${usePrefix ? `${dappConfig.storePrefix}-` : ""}${key}`;

  function getDefaultState() {
    const defaultValue = typeof initialState === "function" ? (initialState as () => S)() : initialState;
    const persistValue = cookies.get(cookieKey);
    const defaultState = persistValue ? (skipDecode ? (persistValue as S) : (JSON.parse(persistValue) as S)) : null;

    return (defaultState ?? defaultValue) as S;
  }

  const [state, setState] = useState<S>(getDefaultState);

  useEffect(
    function updateLocalStorage() {
      if (state === undefined || state === null) {
        cookies.remove(cookieKey);
      } else {
        cookies.set(cookieKey, skipDecode ? `${state}` : JSON.stringify(state ?? ""), { expires: 360 });
      }
    },
    [cookieKey, cookies, key, skipDecode, state]
  );

  return [state, setState];
}
